import {
  getDocumentById,
  getCollection,
  setDocById,
  addDocToCollection,
  updateDocById,
  deleteDocByCollectionAndId,
} from '../_shared/firestore/node/index.js';
import { createCMMSView } from '../views/views.js';
import { getPartById, updatePart } from '../parts/index.js';

const COLLECTION_NAME = 'parts_usage';

async function getPartUsagesByWorkOrder(db, workOrderId, companyId = null) {
  try {
    // Build where conditions
    const whereConditions = [{ key: 'work_order_id', operator: '==', value: workOrderId }];
    
    // Add company_id filter if provided
    if (companyId) {
      whereConditions.push({ key: 'company_id', operator: '==', value: companyId });
    }
    
    const docs = await getCollection(db, COLLECTION_NAME, whereConditions);
    const partsUsage = docs.map((doc) => {
      return { id: doc.$id, ...doc };
    });
    return createCMMSView({ data: partsUsage });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getPartUsagesByCompany(db, companyId) {
  try {
    const docs = await getCollection(db, COLLECTION_NAME, { key: 'company_id', operator: '==', value: companyId });
    const partsUsage = docs.map((doc) => {
      return { id: doc.$id, ...doc };
    });
    return createCMMSView({ data: partsUsage });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function getPartUsageById(db, id) {
  try {
    const partUsage = await getDocumentById(db, COLLECTION_NAME, id);
    if (!partUsage) return createCMMSView({ error: 'Part usage not found' });
    return createCMMSView({ data: { id: partUsage.$id, ...partUsage } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function createPartUsage(db, partUsageData) {
  try {
    const partUsageDoc = {
      ...partUsageData,
      timestamp: partUsageData.timestamp || Date.now(),
      created_at: Date.now(),
      updated_at: Date.now(),
    };

    const docRef = await addDocToCollection(db, COLLECTION_NAME, partUsageDoc);

    return createCMMSView({ data: { id: docRef.id, ...partUsageDoc } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function updatePartUsage(db, id, updateData) {
  try {
    const updateDoc = {
      ...updateData,
      updated_at: Date.now(),
    };

    await updateDocById(db, COLLECTION_NAME, id, updateDoc);

    return createCMMSView({ data: { id, ...updateDoc } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

async function deletePartUsage(db, id) {
  try {
    await deleteDocByCollectionAndId(db, COLLECTION_NAME, id);

    return createCMMSView({ data: { id, deleted: true } });
  } catch (error) {
    return createCMMSView({ error });
  }
}

// Combined function to create part usage and update part stock in one operation
async function usePartInWorkOrder(db, { work_order_id, part_id, quantity, company_id }) {
  try {
    // First, get the current part to check stock
    const partResult = await getPartById(db, part_id);
    if (partResult.error) {
      return createCMMSView({ error: 'Part not found' });
    }
    
    const part = partResult.data;
    
    // Check if we have enough stock
    if (part.stock_qty < quantity) {
      return createCMMSView({ 
        error: `Insufficient stock. Available: ${part.stock_qty}, Requested: ${quantity}` 
      });
    }
    
    // Verify company_id matches
    if (part.company_id !== company_id) {
      return createCMMSView({ 
        error: `Part belongs to different company. Part company: ${part.company_id}, Work order company: ${company_id}` 
      });
    }
    
    // Create the part usage record
    const partUsageData = {
      work_order_id,
      part_id,
      quantity,
      company_id,
      timestamp: Date.now(),
      created_at: Date.now(),
      updated_at: Date.now(),
    };
    
    let usageDocRef;
    try {
      usageDocRef = await addDocToCollection(db, COLLECTION_NAME, partUsageData);
    } catch (error) {
      throw error;
    }
    
    // Update the part stock quantity
    const newStockQty = part.stock_qty - quantity;
    const updateResult = await updatePart(db, part_id, { stock_qty: newStockQty });
    
    if (updateResult.error) {
      // If updating part stock fails, we should ideally rollback the part usage creation
      // For now, we'll just return the error
      return createCMMSView({ 
        error: 'Part usage created but failed to update stock quantity' 
      });
    }
    
    return createCMMSView({ 
      data: { 
        id: usageDocRef.id, 
        ...partUsageData,
        part_name: part.name,
        unit_cost: part.cost,
        total_cost: part.cost * quantity,
        remaining_stock: newStockQty
      }
    });
    
  } catch (error) {
    return createCMMSView({ error: error.message || 'Failed to use part in work order' });
  }
}

export {
  getPartUsagesByWorkOrder,
  getPartUsagesByCompany,
  getPartUsageById,
  createPartUsage,
  updatePartUsage,
  deletePartUsage,
  usePartInWorkOrder,
};
